// "Set your GSTIN / state" on the till (gst-parked task 030, owner decision D4).
//
// Warns, never blocks a sale. Dismissed for the rest of the cashier's shift:
// the dismissal is keyed by the open attendance id, so the next shift shows it
// again. India GST only — a VAT business never sees it.

import { useState } from 'react';
import { useAuthStore } from '@/stores/use-auth-store';
import { useLocationStore } from '@/stores/use-location-store';
import { useShiftStore } from '@/stores/use-shift-store';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { appUrl } from '@/lib/app-url';
import { gstSetupText, gstSetupWarning } from '@/lib/gst-setup';

const DISMISS_KEY = 'bbpos_gst_setup_dismissed';

function readDismissed(): string | null {
  try {
    return localStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

export function GstSetupTillBanner() {
  const profile = useAuthStore((s) => s.profile);
  const currentLocationId = useLocationStore((s) => s.currentLocationId);
  const shiftKey = useShiftStore((s) => s.activeAttendance?.id) ?? 'no-shift';
  const isIndia = useTaxRegime().regime === 'GST_INDIA';
  const [dismissed, setDismissed] = useState(readDismissed);

  if (!profile?.tenant || dismissed === shiftKey) return null;

  // The till bills for one store, so only that store's State matters here.
  const locations = profile.locations ?? [];
  const current = locations.find((l) => l.id === currentLocationId);
  const warning = gstSetupWarning({
    isIndia,
    setting: profile.tenant.gst_registered_setting,
    gstin: profile.tenant.gstin,
    state: profile.tenant.state,
    locations: current ? [current] : locations,
  });
  if (!warning) return null;

  const { title, lines } = gstSetupText(warning);
  const needsBusiness = warning.kind === 'UNREGISTERED_NO_GSTIN' || warning.gstin || warning.state;
  const needsStores = warning.kind === 'MISSING' && warning.locations.length > 0;
  const isOwner = profile.role === 'OWNER';
  const isManager = profile.role === 'MANAGER';

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, shiftKey);
    } catch {
      // Storage blocked — hidden until the page reloads.
    }
    setDismissed(shiftKey);
  };

  return (
    <div
      role="status"
      data-testid="gst-setup-till-banner"
      className="shrink-0 flex items-start gap-3 px-6 py-2 bg-gradient-to-r from-warning-50 to-danger-50 dark:from-neutral-800 dark:to-neutral-800 border-b border-warning-200 dark:border-warning-800 animate-fade-in"
    >
      <span
        className="mt-0.5 w-6 h-6 shrink-0 rounded-md bg-gradient-to-br from-warning-500 to-danger-500 text-white flex items-center justify-center text-xs font-bold"
        aria-hidden="true"
      >
        !
      </span>
      <div className="flex-1 min-w-0 text-xs text-neutral-700 dark:text-neutral-200">
        <p className="font-semibold text-neutral-900 dark:text-white">{title}</p>
        <p>{lines.join(' ')}</p>
        <div className="flex flex-wrap gap-3 mt-1">
          {needsBusiness && isOwner && (
            <a
              href={appUrl('admin', '/settings')}
              className="font-semibold text-brand-600 dark:text-brand-400 hover:underline transition-all duration-200"
            >
              Add GSTIN and State
            </a>
          )}
          {needsStores && (isOwner || isManager) && (
            <a
              href={appUrl('admin', '/locations')}
              className="font-semibold text-brand-600 dark:text-brand-400 hover:underline transition-all duration-200"
            >
              Open Locations
            </a>
          )}
          {needsBusiness && isManager && <span>Ask the owner to add the GSTIN and State.</span>}
          {!isOwner && !isManager && <span>Ask your manager or owner to fix this.</span>}
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 text-xs font-medium text-neutral-500 hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-white transition-all duration-200"
      >
        Hide for this shift
      </button>
    </div>
  );
}
